import { claimsModalEditingModes } from '../claimsModal/claimsModal.constants';
import { createError, normalizeIdentifier } from '../../../../../lib/util';


class ClaimDisplayBox {
  constructor(
    $scope,
    $translate,
    $q,
    APIService,
    LogService,
    ClaimService,
    PositionService,
    DataSyncService,
    TaskQueueService,
    DateTimeService,
    showClaimsModal,
    showConfirmModal,
    DEBUG,
    moment
  ) {
    "ngInject";
    this.$scope           = $scope;
    this.$translate       = $translate;
    this.$q = $q;
    this.APIService       = APIService;
    this.LogService       = LogService;
    this.ClaimService = ClaimService;
    this.PositionService = PositionService;
    this.DataSyncService = DataSyncService;
    this.TaskQueueService = TaskQueueService;
    this.DateTimeService  = DateTimeService;
    this.moment           = moment;
    this.DEBUG = DEBUG;
    this.showClaimsModal = showClaimsModal;
    this.showConfirmModal = showConfirmModal;
  }

  set staffUser(user) {
    this._staffUser = user;
  }

  get staffUser() {
    return this._staffUser;
  }

  set claim(claim) {
    this._claim = claim;
    this._claimDateShifted = null;
  }

  get claim() {
    return this._claim;
  }

  get claimDateShifted() {
    if (!this._claimDateShifted) {
      if (this.claim && this.claim.claim_date) {
        let destTimezone = this.claim.event && this.claim.event.timezoneName;
        this._claimDateShifted = this.DateTimeService.shiftDateObjectFromLocalToTimezoneOrServerTimezone(this.claim.claim_date, destTimezone);
      }
    }
    return this._claimDateShifted;
  }

  get canEditAndResubmit() {
    if (this.claim.isManualClaim) { 
      return false;
    }
    return this.claim && this.claim.isDeclined && !this.claim.isResubmitted && !this.claim.enqueued;
  }

  get canWithdraw() {
    if (this.claim.isManualClaim) { 
      return false;
    }
    return this.claim && this.claim.isSecondaryClaim && this.claim.isDeclined && !this.claim.isWithdrawn && !this.claim.enqueued;
  }

  get isResubmitted() {
    return this.claim && this.claim.isResubmitted;
  }

  get isWithdrawn() {
    return this.claim && this.claim.isWithdrawn;
  }

  get debug() {
    return this.DEBUG;
  }

  editAndResubmit() {
    const staffUser = this.staffUser;
    let claim = this.claim;
    let event = claim.event;
    let position = claim.position;

    const claimId = claim && claim.id;
    const positionId = claim && normalizeIdentifier(claim.events_staffing_days_positions_id);

    /**
     * Find position by ID, either off of the staffUser object, or through PositionService.getByIdForStaffingUser()
     * @param positionId
     * @returns {*}
     */
    const attemptToFindPositionById = positionId => {
      positionId = normalizeIdentifier(positionId);
      return this.$q.when(null)
        .then(() => staffUser.positions.find(p => String(normalizeIdentifier(p.id)) === String(positionId)))
        .then(foundPosition => {
          return foundPosition || this.PositionService.getByIdForStaffingUser(staffUser, positionId);
        });
    };

    const ensureDataLoaded = () => {
      if (!claimId) {
        this.LogService.error("ClaimDisplayBox.editAndResubmit() missing claim");
        return this.$q.reject(createError({
          error: 'EditAndResubmitMissingClaim',
          message: 'Missing claim',
        }));
      }
      if (!event) {
        this.LogService.error("ClaimDisplayBox.editAndResubmit() missing event");
        return this.$q.reject(createError({
          error: 'EditAndResubmitMissingEvent',
          message: 'Missing event',
        }));
      }
      return this.$q.when(position)
        .then(foundPosition => foundPosition || attemptToFindPositionById(positionId))
        .then(foundPosition => {
          if (foundPosition) {
            // Good!
            position = foundPosition;
            return foundPosition;
          } else {
            // Still not able to find position; check data window
            if (this.DataSyncService.isSyncWindowSetToShowAll) {
              this.LogService.error(`ClaimDisplayBox.editAndResubmit ensureDataLoaded sync window is aleady set to 'all' so can't try to reload`);
              return this.$q.reject(createError({
                error: 'EditAndResubmitMissingPosition',
                message: "Missing position for edit & resubmit, and we're already showing all data",
              }));
            } else {
              this.LogService.debug(`ClaimDisplayBox.editAndResubmit ensureDataLoaded trying to switch sync window to 'all'`);
              return this.$translate('SYNC_WINDOW_CLAIM_POSITION_NOT_LOADED_SWITCH_TO_ALL')
                .then(message => this.showConfirmModal(message))
                .then(() => this.DataSyncService.switchSyncWindowToShowAllAndSync())
                /*
                 * NOTE: We can probably remove the following line. I believe what happens is that the data sync causes
                 * our claim-display-box instance to be deleted and recreated, and maybe angular is smart enough to
                 * cancel the remaining promises? Either way it doesn't seem to continue and show the modal as we're
                 * trying to do.
                 */
                .then(() => resetVariablesAfterDataSync())
            }
          }
        })
    };

    const resetVariablesAfterDataSync = () => {
      return this.ClaimService.getById(claimId)
        .then(foundClaim => {
          if (!foundClaim) {
            return this.$q.reject(createError({
              error: 'ClaimDisplayBoxUnableToLoadClaimAfterSync',
              message: `Unable to load claim id ${claimId} after data sync`,
            }));
          }
          claim = foundClaim;
          event = claim.event;
          return claim.position;
        })
        .then(foundClaimPosition => foundClaimPosition || attemptToFindPositionById(positionId))
        .then(foundPosition => {
          if (foundPosition) {
            position = foundPosition;
          } else {
            return this.$q.reject(createError({
              error: 'ClaimDisplayBoxUnableToFindPositionAfterSync',
              message: `Unable to load position id ${positionId} for claim id ${claimId} even after data sync`,
            }));
          }
        })
    };

    const showModal = () => {
      const props = {
        staffUser,
        event,
        position,
        primaryClaim: claim.isPrimaryClaim ? claim : null,
        secondaryClaims: claim.isPrimaryClaim ? [] : [claim],
        modalClass: 'payroll-modal',
        editingMode: claimsModalEditingModes.resubmit,
      };
      return this.showClaimsModal(props);
    };

    return ensureDataLoaded()
      .then(() => showModal());
  }

  withdraw() {
    return this.$translate('CONFIRM_WITHDRAW_CLAIM')
      .then(message => this.showConfirmModal(message))
      .then(() => {
        let task = this.TaskQueueService.createTask('ClaimWithdrawTask');
        task.setClaim(this.claim);
        if (task.canExecute) {
          return task.execute()
            .then(() => {
              this.$scope.$emit('sync');
            });
        } else {
          return this.TaskQueueService.enqueue(task)
            .then(result => {
              this.claim.enqueued = true;
              return result;
            });
        }
      });
  }
}

export default ClaimDisplayBox;

